import type { EnemyRoleId } from "../content/schemas";
import type { LastDamageSource } from "./death-report";
import type { RunProgress } from "./progression";
import { scoreForEnemyKill } from "./progression";
import { cloneRunDebriefStats, createRunDebriefStats, type RunDebriefStats } from "./run-debrief";

export type { RunDebriefStats } from "./run-debrief";

export const CHAIN_WINDOW_S = 3;
export const MULTI_KILL_WINDOW_S = 0.75;
export const COMBO_BONUS_PER_LINK = 0.08;
export const DOUBLE_KILL_BONUS = 150;
export const TRIPLE_KILL_BONUS = 400;
export const CHAIN_MILESTONE_BONUS = 75;
export const OVERKILL_BONUS = 80;
export const TACTICIAN_BONUS = 120;
export const SALVAGE_SCORE_PER = 2;
export const TIME_SCORE_PER_INTERVAL = 25;

export type ComboStyleTag = "double" | "triple" | "overkill" | "tactician" | "chain";

export type ScoreEventKind =
  "kill" | "chain" | "double_kill" | "triple_kill" | "overkill" | "tactician" | "salvage" | "time";

export type ScoreBreakdown = {
  kills: number;
  chain: number;
  multiKill: number;
  style: number;
  salvage: number;
  time: number;
};

export type ComboState = {
  chainTimer: number;
  chainCount: number;
  multiKillTimer: number;
  multiKillCount: number;
  comboMultiplier: number;
  recentTags: ComboStyleTag[];
  /** @deprecated Retained in snapshots so older saves remain loadable. */
  bandShiftTimer: number;
};

export type ScoreFeedEntry = {
  id: number;
  tick: number;
  kind: ScoreEventKind;
  label: string;
  points: number;
};

export type ScorePopup = {
  id: number;
  x: number;
  y: number;
  label: string;
  points: number;
  life: number;
  maxLife: number;
};

export type LetterGrade = "S" | "A" | "B" | "C" | "D";

export type RunSummary = {
  outcome: "extracted" | "dead";
  score: number;
  grade: LetterGrade;
  level: number;
  simTime: number;
  breakdown: ScoreBreakdown;
  bestStreak: number;
  bestChain: number;
  killsByRole: Partial<Record<EnemyRoleId, number>>;
  takenNodes: string[];
  salvageCollected: number;
  totalKills: number;
  /** Populated on death when a killing blow source is known. */
  deathReport: LastDamageSource | null;
  /** Salvage banked this extract (0 on death). */
  salvageBanked: number;
  /** Per-run analytics for debrief graphs. */
  debrief: RunDebriefStats;
};

export type KillScoreContext = {
  progress: RunProgress;
  combo: ComboState;
  breakdown: ScoreBreakdown;
  killsByRole: Partial<Record<EnemyRoleId, number>>;
  role: EnemyRoleId;
  elite: boolean;
  x: number;
  y: number;
  tick: number;
  overkill: boolean;
  tactician: boolean;
  nextEventId: number;
  nextPopupId: number;
  eliteScoreMult?: number;
};

export type KillScoreResult = {
  scoreGained: number;
  feed: ScoreFeedEntry[];
  popups: ScorePopup[];
  nextEventId: number;
  nextPopupId: number;
};

export function createComboState(): ComboState {
  return {
    chainTimer: 0,
    chainCount: 0,
    multiKillTimer: 0,
    multiKillCount: 0,
    comboMultiplier: 1,
    recentTags: [],
    bandShiftTimer: 0,
  };
}

export function createScoreBreakdown(): ScoreBreakdown {
  return { kills: 0, chain: 0, multiKill: 0, style: 0, salvage: 0, time: 0 };
}

export function tickCombo(combo: ComboState, dt: number): void {
  if (combo.chainTimer > 0) {
    combo.chainTimer -= dt;
    if (combo.chainTimer <= 0) {
      combo.chainTimer = 0;
      combo.chainCount = 0;
      combo.comboMultiplier = 1;
    }
  }
  if (combo.multiKillTimer > 0) {
    combo.multiKillTimer -= dt;
    if (combo.multiKillTimer <= 0) {
      combo.multiKillTimer = 0;
      combo.multiKillCount = 0;
    }
  }
  // Legacy vertical-style timer is intentionally no longer updated.
  combo.comboMultiplier = comboMultiplierFor(combo.chainCount);
}

export function resetComboOnDamage(combo: ComboState): void {
  combo.chainTimer = 0;
  combo.chainCount = 0;
  combo.multiKillTimer = 0;
  combo.multiKillCount = 0;
  combo.comboMultiplier = 1;
  combo.recentTags = [];
}

export function comboMultiplierFor(chainCount: number): number {
  if (chainCount <= 1) return 1;
  return 1 + (chainCount - 1) * COMBO_BONUS_PER_LINK;
}

export function processEnemyKill(ctx: KillScoreContext): KillScoreResult {
  const { progress, combo, breakdown, role, elite, x, y, tick } = ctx;
  const feed: ScoreFeedEntry[] = [];
  const popups: ScorePopup[] = [];
  let eventId = ctx.nextEventId;
  let popupId = ctx.nextPopupId;

  if (combo.multiKillTimer > 0) combo.multiKillCount += 1;
  else combo.multiKillCount = 1;
  combo.multiKillTimer = MULTI_KILL_WINDOW_S;

  if (combo.chainTimer > 0) combo.chainCount += 1;
  else combo.chainCount = 1;
  combo.chainTimer = CHAIN_WINDOW_S;
  combo.comboMultiplier = comboMultiplierFor(combo.chainCount);

  ctx.killsByRole[role] = (ctx.killsByRole[role] ?? 0) + 1;

  const eliteMult = elite ? (ctx.eliteScoreMult ?? 1) : 1;
  const base = Math.round(scoreForEnemyKill(progress, role, elite) * eliteMult);
  const comboBonus = Math.round(base * (combo.comboMultiplier - 1));
  let total = base + comboBonus;
  breakdown.kills += base;
  breakdown.chain += comboBonus;

  const roleLabel = elite ? `${role} elite` : role;
  const pushFeed = (kind: ScoreEventKind, label: string, points: number): void => {
    feed.push({ id: eventId, tick, kind, label, points });
    eventId += 1;
  };
  const pushPopup = (label: string, points: number): void => {
    popups.push({
      id: popupId,
      x,
      y,
      label,
      points,
      life: 1.2,
      maxLife: 1.2,
    });
    popupId += 1;
  };

  pushFeed("kill", roleLabel, base);
  pushPopup(`+${base}`, base);

  if (comboBonus > 0) {
    pushFeed("chain", `CHAIN ×${combo.chainCount}`, comboBonus);
  }

  if (combo.chainCount > 0 && combo.chainCount % 5 === 0) {
    total += CHAIN_MILESTONE_BONUS;
    breakdown.chain += CHAIN_MILESTONE_BONUS;
    pushFeed("chain", `CHAIN ${combo.chainCount}!`, CHAIN_MILESTONE_BONUS);
    pushPopup("CHAIN!", CHAIN_MILESTONE_BONUS);
  }

  if (combo.multiKillCount === 2) {
    total += DOUBLE_KILL_BONUS;
    breakdown.multiKill += DOUBLE_KILL_BONUS;
    combo.recentTags = ["double", ...combo.recentTags].slice(0, 4) as ComboStyleTag[];
    pushFeed("double_kill", "DOUBLE", DOUBLE_KILL_BONUS);
    pushPopup("DOUBLE", DOUBLE_KILL_BONUS);
  } else if (combo.multiKillCount >= 3) {
    total += TRIPLE_KILL_BONUS;
    breakdown.multiKill += TRIPLE_KILL_BONUS;
    combo.recentTags = ["triple", ...combo.recentTags].slice(0, 4) as ComboStyleTag[];
    pushFeed("triple_kill", "TRIPLE", TRIPLE_KILL_BONUS);
    pushPopup("TRIPLE", TRIPLE_KILL_BONUS);
  }

  if (ctx.overkill) {
    total += OVERKILL_BONUS;
    breakdown.style += OVERKILL_BONUS;
    combo.recentTags = ["overkill", ...combo.recentTags].slice(0, 4) as ComboStyleTag[];
    pushFeed("overkill", "OVERKILL", OVERKILL_BONUS);
    pushPopup("OVERKILL", OVERKILL_BONUS);
  }

  if (ctx.tactician) {
    total += TACTICIAN_BONUS;
    breakdown.style += TACTICIAN_BONUS;
    combo.recentTags = ["tactician", ...combo.recentTags].slice(0, 4) as ComboStyleTag[];
    pushFeed("tactician", "TACTICIAN", TACTICIAN_BONUS);
    pushPopup("TACTICIAN", TACTICIAN_BONUS);
  }

  progress.score += total;
  return { scoreGained: total, feed, popups, nextEventId: eventId, nextPopupId: popupId };
}

export function scoreForSalvagePickup(
  progress: RunProgress,
  breakdown: ScoreBreakdown,
  amount: number,
  tick: number,
  nextEventId: number,
): { points: number; feed: ScoreFeedEntry | null } {
  const points = amount * SALVAGE_SCORE_PER;
  if (points <= 0) return { points: 0, feed: null };
  progress.score += points;
  breakdown.salvage += points;
  return {
    points,
    feed: {
      id: nextEventId,
      tick,
      kind: "salvage",
      label: `Salvage +${amount}`,
      points,
    },
  };
}

export function scoreForTimeSurvived(
  progress: RunProgress,
  breakdown: ScoreBreakdown,
  intervals: number,
  tick: number,
  nextEventId: number,
): { points: number; feed: ScoreFeedEntry | null } {
  const points = intervals * TIME_SCORE_PER_INTERVAL;
  if (points <= 0) return { points: 0, feed: null };
  progress.score += points;
  breakdown.time += points;
  return {
    points,
    feed: {
      id: nextEventId,
      tick,
      kind: "time",
      label: "Survival bonus",
      points,
    },
  };
}

export function letterGrade(score: number, level: number, simTime: number): LetterGrade {
  const expected = 500 * level + simTime * 8;
  const ratio = score / Math.max(1, expected);
  if (ratio >= 1.4) return "S";
  if (ratio >= 1.1) return "A";
  if (ratio >= 0.85) return "B";
  if (ratio >= 0.6) return "C";
  return "D";
}

export function buildRunSummary(
  outcome: "extracted" | "dead",
  progress: RunProgress,
  breakdown: ScoreBreakdown,
  _combo: ComboState,
  killsByRole: Partial<Record<EnemyRoleId, number>>,
  simTime: number,
  salvageCollected: number,
  opts?: {
    deathReport?: LastDamageSource | null;
    salvageBanked?: number;
    debrief?: RunDebriefStats;
  },
): RunSummary {
  const totalKills = Object.values(killsByRole).reduce((sum, n) => sum + (n ?? 0), 0);
  return {
    outcome,
    score: progress.score,
    grade: letterGrade(progress.score, progress.level, simTime),
    level: progress.level,
    simTime,
    breakdown: { ...breakdown },
    bestStreak: progress.bestKillStreak,
    bestChain: progress.bestChain,
    killsByRole: { ...killsByRole },
    takenNodes: [...progress.takenNodes],
    salvageCollected,
    totalKills,
    deathReport: opts?.deathReport ?? null,
    salvageBanked: opts?.salvageBanked ?? 0,
    debrief: opts?.debrief ? cloneRunDebriefStats(opts.debrief) : createRunDebriefStats(),
  };
}

export function isOverkill(damage: number, enemyMaxHull: number): boolean {
  return damage > enemyMaxHull * 1.5;
}

export function isTacticianKill(playerShield: number): boolean {
  return playerShield <= 0;
}

export function roleLabel(role: EnemyRoleId, elite: boolean): string {
  return elite ? `${role} ★` : role;
}
